import React, { useCallback, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { DeprecatedOnboardingRecipeFilters } from '@mindfulchefuk/features/Onboarding/components/RecipeFilters/DeprecatedOnboardingRecipeFilters'
import { ChooseRecipesBanner } from '@mindfulchefuk/features/ChooseRecipes/ChooseRecipesBanner'
import { getRecipesByDateAndPortion } from '@mindfulchefuk/actions/recipesActions'
import { RootState } from '@mindfulchefuk/types/store'
import { useOnDateChange } from '@mindfulchefuk/features/Onboarding/hooks/useOnDateChange'
import { useOnMealPlanChange } from '@mindfulchefuk/features/Onboarding/hooks/useOnMealPlanChange'
import { useOnPortionCountChange } from '@mindfulchefuk/features/Onboarding/hooks/useOnPortionCountChange'
import { useOnSortByChange } from '@mindfulchefuk/features/Onboarding/hooks/useOnSortByChange'
import {
  IS_PENDING,
  TOTAL_RECIPES_AVAILABLE_PER_WEEK,
} from '@mindfulchefuk/constants'
import { Box, Grid, Text } from '@mindfulchefuk/design-system'
import { useResetInvalidDeliveryDate } from '@mindfulchefuk/features/Onboarding/hooks/useResetInvalidDeliveryDate'
import { useApplyVoucher } from '@mindfulchefuk/features/Basket'
import { useRepopulateBasketOnFilter } from '@mindfulchefuk/features/Basket/hooks/useRepopulateBasketOnFilter'
import { logPageViewTikTok } from '@mindfulchefuk/utils/analytics'
import { PageLoader } from '@mindfulchefuk/components/Loader'
import { useChooseRecipesRedirect } from '@mindfulchefuk/features/Onboarding/hooks/useChooseRecipesRedirect'
import { getRecipeById } from '@mindfulchefuk/features/Onboarding/selectors/getRecipeWithQuantityById'
import mealPlanConfig, { TMealPlan } from '@mindfulchefuk/config/mealPlanConfig'
import { PortionCount } from '@mindfulchefuk/config/portionCountConfig'
import { useDeprecatedChooseRecipesActions } from '@mindfulchefuk/features/Onboarding/hooks/useDeprecatedChooseRecipeActions'
import { getMinimumMaximumRecipesForPortion } from '@mindfulchefuk/services/getMinimumMaximumRecipesForPortion'
import { RecipeModalWithCTAs } from '@mindfulchefuk/features/Recipes/components/RecipeModal/RecipeModalWithCTAs'
import { ProductAnnouncementModal } from '@mindfulchefuk/features/CMS/components'
import { setDeliveryDate } from '@mindfulchefuk/actions/basketActions'
import { useAvailableDates } from '@mindfulchefuk/features/Operations/hooks/useAvailableDates'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'
import { getPageSourceForChooseRecipesViewed } from '@mindfulchefuk/utils/analytics/amplitude/helpers/getPageSourceForChooseRecipesViewed'
import { DeprecreatedRecipesPage } from '@mindfulchefuk/onboarding/components/chooseRecipes/RecipesPage/DeprecreatedRecipesPage'

export const DeprecatedChooseRecipesPage = () => {
  const {
    items,
    itemIds,
    portionCount,
    selectedMealPlan,
    recipesIsPending,
    allRecipes,
    sortedBy,
    selectedDelivery,
  } = useSelector(
    (store: RootState) => ({
      items: store.basket.items,
      itemIds: store.basket.itemIds,
      portionCount: store.basket.portionCount,
      selectedMealPlan: store.basket.selectedMealPlan,
      selectedDelivery: store.basket.selectedDeliveryDate,
      recipesIsPending: store.recipes[IS_PENDING],
      allRecipes: store.recipes.allRecipes,
      sortedBy: store.recipes.sortedBy,
      excludedFoodGroups: store.recipes.excludedFoodGroups,
    }),
    shallowEqual
  )

  const router = useRouter()
  const dispatch = useDispatch()

  const {
    recipes: { firstAvailableDeliveryDate },
  } = useAvailableDates()

  useEffect(() => {
    if (!selectedDelivery) {
      dispatch(setDeliveryDate(firstAvailableDeliveryDate))
    }
  }, [selectedDelivery, dispatch, firstAvailableDeliveryDate])

  const recipeId = router.query.recipe as string

  const recipeData = useSelector(getRecipeById(recipeId))

  useApplyVoucher(router.query.savedVoucherCode as string)
  useResetInvalidDeliveryDate({ isCheckoutFlow: false })
  useRepopulateBasketOnFilter()

  const {
    handleClickFromRecipeCard,
    handleClickFromModal,
    handleRemoveRecipe,
  } = useDeprecatedChooseRecipesActions()

  const onDateChange = useOnDateChange()
  const onMealPlanChange = useOnMealPlanChange()
  const onPortionCountChange = useOnPortionCountChange()
  const onSortByChange = useOnSortByChange()

  const { showLoader } = useChooseRecipesRedirect()

  const { minimumRecipes, maximumRecipes } =
    getMinimumMaximumRecipesForPortion(portionCount)

  const getExcludedFoodGroups = useCallback((mealPlan: string) => {
    const plan = mealPlanConfig.find((p) => p.value === mealPlan)
    return plan ? plan.excludedFoodGroups : []
  }, [])

  const { initialMealPlan, initialPortionCount } = router.query

  const isInitialMealPlanValid = !!mealPlanConfig.find(
    (plan) => plan.value === initialMealPlan
  )

  const isInitialPortionCountValid =
    initialPortionCount === '1' ||
    initialPortionCount === '2' ||
    initialPortionCount === '4'

  useEffect(() => {
    if (!selectedDelivery) return
    dispatch(
      getRecipesByDateAndPortion({
        date: selectedDelivery,
        portionCount,
        mealPlan: selectedMealPlan as TMealPlan,
        excludedFoodGroups: getExcludedFoodGroups(selectedMealPlan),
      })
    )
  }, [
    selectedDelivery,
    portionCount,
    selectedMealPlan,
    dispatch,
    getExcludedFoodGroups,
  ])

  useEffect(() => {
    if (isInitialMealPlanValid) {
      onMealPlanChange(initialMealPlan as TMealPlan)
    }

    if (isInitialPortionCountValid) {
      onPortionCountChange(parseInt(initialPortionCount, 10) as PortionCount)
    }
  }, [
    initialMealPlan,
    initialPortionCount,
    isInitialMealPlanValid,
    isInitialPortionCountValid,
    onMealPlanChange,
    onPortionCountChange,
  ])

  useEffect(() => {
    const { pageSource, rawPageSource } = getPageSourceForChooseRecipesViewed()

    logPageViewTikTok()
    trackAnalyticsEvent({
      name: 'choose recipes viewed',
      data: {
        'page source': pageSource,
        rawPageSource,
      },
    })
  }, [])

  const isLoading =
    showLoader || !selectedDelivery || !firstAvailableDeliveryDate

  if (isLoading) {
    return (
      <Box position="fixed" top={0} left={0} bg="white">
        <PageLoader />
      </Box>
    )
  }

  return (
    <Grid as="article" templateColumns="1fr" gap={16}>
      <Text
        variant={{ base: 'heading-md', sm: 'heading-lg' }}
        as="h1"
        id="choose-recipes-heading"
      >
        Choose {minimumRecipes} to {maximumRecipes} delicious healthy recipes
      </Text>
      <Text variant="body-lg" as="h2" id="choose-recipes-subheading">
        From {TOTAL_RECIPES_AVAILABLE_PER_WEEK} recipes to pick from, changing
        every week. Skip a week or cancel at any time.
      </Text>
      <Box mx={[-24, 0]}>
        <ChooseRecipesBanner />
      </Box>
      <DeprecatedOnboardingRecipeFilters
        portionCount={portionCount}
        mealPlan={selectedMealPlan as TMealPlan}
        sortBy={sortedBy}
        date={selectedDelivery}
        onDateChange={onDateChange}
        onMealPlanChange={onMealPlanChange}
        onPortionCountChange={onPortionCountChange}
        onSortByChange={onSortByChange}
      />
      <DeprecreatedRecipesPage
        onAddRecipe={handleClickFromRecipeCard}
        onRemoveRecipe={handleRemoveRecipe('recipe')}
        recipes={allRecipes}
        recipeIds={itemIds}
        maximumRecipes={maximumRecipes}
        recipesIsPending={recipesIsPending}
      />
      {recipeData && (
        <RecipeModalWithCTAs
          recipe={recipeData}
          maximumRecipes={maximumRecipes}
          totalRecipesInCart={items.length}
          onIncrement={handleClickFromModal}
          onDecrement={handleRemoveRecipe('modal')}
        />
      )}

      <ProductAnnouncementModal page="page-recipe-onboarding" />
    </Grid>
  )
}
